import React, { useEffect } from "react";
import { Modal, Spin, Typography } from "antd";
import "./ResultPopup.scss";

const { Title, Text } = Typography;

/** Auto-dismiss delay (ms) when the user does not close a finished popup. */
const AUTO_CLOSE_MS = 15000;

export type ResultStatus = "loading" | "success" | "error";

export interface IResultPopupProps {
    open: boolean;
    status: ResultStatus;
    message: React.ReactNode;
    /** Heading override; defaults are derived from `status`. */
    title?: string;
    onClose: () => void;
}

const DEFAULT_TITLES: Record<ResultStatus, string> = {
    loading: "Please wait",
    success: "Success",
    error: "Submission Failed",
};

/** Animated success checkmark. */
const SuccessMark: React.FC = () => (
    <svg className="result-popup__icon" viewBox="0 0 52 52" aria-hidden="true">
        <circle className="result-popup__circle result-popup__circle--success" cx="26" cy="26" r="25" fill="none" />
        <path className="result-popup__check" fill="none" d="M14.1 27.2l7.1 7.2 16.7-16.8" />
    </svg>
);

/** Animated error cross. */
const ErrorMark: React.FC = () => (
    <svg className="result-popup__icon" viewBox="0 0 52 52" aria-hidden="true">
        <circle className="result-popup__circle result-popup__circle--error" cx="26" cy="26" r="25" fill="none" />
        <line className="result-popup__cross-line" x1="17" y1="17" x2="35" y2="35" />
        <line className="result-popup__cross-line result-popup__cross-line--second" x1="35" y1="17" x2="17" y2="35" />
    </svg>
);

/**
 * Modal that shows an animated result. Finished states (success/error) can be
 * closed manually and auto-close after 15s; the loading state stays open and
 * cannot be dismissed until the caller updates the status.
 */
const ResultPopup: React.FC<IResultPopupProps> = ({ open, status, message, title, onClose }) => {
    const isLoading = status === "loading";

    useEffect(() => {
        if (!open || isLoading) return;
        const timer = setTimeout(onClose, AUTO_CLOSE_MS);
        return () => clearTimeout(timer);
    }, [open, isLoading, onClose]);

    return (
        <Modal
            open={open}
            onCancel={onClose}
            footer={null}
            centered
            width={420}
            closable={!isLoading}
            maskClosable={false}
            keyboard={!isLoading}
        >
            <div className="result-popup">
                {isLoading && (
                    <div className="result-popup__spinner">
                        <Spin size="large" />
                    </div>
                )}
                {status === "success" && <SuccessMark />}
                {status === "error" && <ErrorMark />}
                <Title level={4} className="result-popup__title">
                    {title ?? DEFAULT_TITLES[status]}
                </Title>
                <Text className="result-popup__message">{message}</Text>
            </div>
        </Modal>
    );
};

export default ResultPopup;
